import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";

const plusPattern = `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Crect x='17' y='10' width='6' height='20' rx='2' fill='white'/%3E%3Crect x='10' y='17' width='20' height='6' rx='2' fill='white'/%3E%3C/svg%3E")`;

const sections = [
  {
    id: "cancellation",
    label: "A",
    title: "Cancellation Policy",
    color: "bg-[#eaf4f3] text-[#1e4a46]",
    activeColor: "bg-[#1e4a46] text-white",
    content: (
      <div className="flex flex-col gap-3">
        <p className="text-[13px] sm:text-[14px] font-semibold text-[#1e4a46]">
          1. How can I cancel an order?
        </p>
        <div className="flex gap-3">
          <span className="flex-shrink-0 w-1 self-stretch bg-[#ff601e] rounded-full opacity-60" />
          <p className="text-[13px] sm:text-[14px] text-[#374747] leading-[1.85] text-justify">
            <span className="font-semibold">1.1 Medicines and Healthcare Products</span>
            <br />
            An order for medicines or healthcare products can be cancelled by reaching out to our customer care number{" "}
            <span className="font-semibold text-[#1e4a46]">7799923501</span> before it is marked as "Out for Delivery".
          </p>
        </div>
      </div>
    ),
  },
  {
    id: "return",
    label: "B",
    title: "Return Policy",
    color: "bg-[#eaf4f3] text-[#1e4a46]",
    activeColor: "bg-[#1e4a46] text-white",
    content: (
      <div className="flex flex-col gap-5">
        {/* Return windows */}
        <div>
          <p className="text-[13px] sm:text-[14px] font-semibold text-[#1e4a46] mb-3">
            1. What is the time period for return of orders?
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="bg-[#f8fafa] border border-[#e2eaea] rounded-xl p-4">
              <p className="text-[12px] font-semibold uppercase tracking-wide text-[#8faba9] mb-1">1.1 Medicine Orders</p>
              <p className="text-[13px] text-[#374747] leading-relaxed">
                Return window ranges from <span className="font-semibold text-[#1e4a46]">0–7 days</span> from the date of delivery. Check the product page for specific policy before placing any order.
              </p>
            </div>
            <div className="bg-[#f8fafa] border border-[#e2eaea] rounded-xl p-4">
              <p className="text-[12px] font-semibold uppercase tracking-wide text-[#8faba9] mb-1">1.2 Healthcare Products</p>
              <p className="text-[13px] text-[#374747] leading-relaxed">
                Return/replacement window ranges from <span className="font-semibold text-[#1e4a46]">0–10 days</span> from the date of delivery. Check the product page for specific policy.
              </p>
            </div>
          </div>
          <p className="text-[12px] text-[#607d7b] italic mt-2">
            Note: Currently, the Company is not charging any shipping charges for returns.
          </p>
        </div>

        {/* Non-returnable */}
        <div>
          <p className="text-[13px] sm:text-[14px] font-semibold text-[#1e4a46] mb-3">
            2. Which Products are not eligible for Returns?
          </p>
          <div className="bg-[#fff3ee] border border-[#ffd5c2] rounded-xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ff601e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
              </svg>
              <p className="text-[12px] font-semibold text-[#ff601e]">Products shall NOT be eligible for return in the following circumstances:</p>
            </div>
            <ul className="flex flex-col gap-2">
              {[
                "Item has been opened, partially used or disfigured",
                "Product has been tampered with",
                "Product packaging / box / seal has been tampered with — do not accept if package appears tampered",
                "Product is mentioned as non-returnable on the product detail page",
                "Any accessories/freebies supplied with the product are missing",
                "Product does not have serial number / UPC number / barcode affixed",
                "Damage/defect not covered under the manufacturer's warranty",
                "Product is without original packing and accessories/freebies",
                "Product is damaged due to misuse",
                "Personal care, baby care, food & nutrition, healthcare devices, sexual wellness, diapers, health drinks, glucometers, health monitors, etc.",
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-2.5 text-[13px] text-[#374747] leading-relaxed">
                  <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-[#ff601e] mt-1.5" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Additional note */}
        <div className="flex gap-3">
          <span className="flex-shrink-0 w-1 self-stretch bg-[#ff601e] rounded-full opacity-60" />
          <p className="text-[13px] sm:text-[14px] text-[#374747] leading-[1.85] text-justify">
            <span className="font-semibold">3.1 </span>
            Items marked as "non-returnable" on the product detail page or items whose return window has expired cannot be returned. If such an item is returned along with another returnable item, MediSYS will disallow a refund or return of the "Non-Returnable" product upon verification.
          </p>
        </div>

        {/* How to return */}
        <div>
          <p className="text-[13px] sm:text-[14px] font-semibold text-[#1e4a46] mb-3">4. How to Return?</p>
          <div className="flex flex-col gap-3">
            {[
              { n: "4.1", text: `Place a return request by calling customer support at 7799923501.` },
              { n: "4.2", text: "After your return request is processed, we will pick up the return items — Local Cities*: within 24–48 hours from approval. Other Cities: 4–5 business days from approval (may be longer depending on courier partners)." },
              { n: "4.3", text: "Keep the return package ready in its original packaging with all labels intact. Keep a copy of the invoice/bill handy for verification." },
            ].map((item) => (
              <div key={item.n} className="flex gap-3">
                <span className="flex-shrink-0 w-1 self-stretch bg-[#5fbdb3] rounded-full opacity-70" />
                <p className="text-[13px] sm:text-[14px] text-[#374747] leading-[1.85] text-justify">
                  <span className="font-semibold">{item.n} </span>{item.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    ),
  },
  {
    id: "refund",
    label: "C",
    title: "Refund Policy",
    color: "bg-[#eaf4f3] text-[#1e4a46]",
    activeColor: "bg-[#1e4a46] text-white",
    content: (
      <div className="flex flex-col gap-5">
        <p className="text-[13px] sm:text-[14px] font-semibold text-[#1e4a46]">
          1. When can I expect the amount to be refunded?
        </p>

        {[
          {
            n: "1.1",
            text: "The refund amount will be finalised after completion of verification. Once pick-up is completed and the product is verified by the seller, a refund of the total amount paid will be initiated for eligible returns.",
          },
          {
            n: "1.2",
            text: "Refund is initiated only post successful verification. Verification may take — Local Cities*: 48 hours from pick-up. Other Cities: 8–10 business days from pick-up.",
          },
        ].map((item) => (
          <div key={item.n} className="flex gap-3">
            <span className="flex-shrink-0 w-1 self-stretch bg-[#ff601e] rounded-full opacity-60" />
            <p className="text-[13px] sm:text-[14px] text-[#374747] leading-[1.85] text-justify">
              <span className="font-semibold">{item.n} </span>{item.text}
            </p>
          </div>
        ))}

        {/* Refund table */}
        <div>
          <p className="text-[13px] sm:text-[14px] text-[#374747] mb-3">
            <span className="font-semibold">1.3 </span>
            Expected timeframes after verification:
          </p>
          <div className="overflow-x-auto rounded-2xl border border-[#e2eaea] shadow-[0_2px_8px_rgba(22,44,42,0.06)]">
            <table className="w-full border-collapse text-[13px] sm:text-[14px]">
              <thead>
                <tr className="bg-[#1e4a46]">
                  <th className="text-left px-5 py-3 text-white font-semibold">Method of Payment</th>
                  <th className="text-left px-5 py-3 text-white font-semibold">Refund Timeframe</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { method: "Cash on Delivery",  time: "1 business day" },
                  { method: "NEFT",              time: "1–3 business days" },
                  { method: "Online Refund",     time: "7–10 business days (subject to bank turnaround time and RBI Guidelines)" },
                  { method: "Other Wallets",     time: "2–3 business days" },
                ].map((row, i) => (
                  <tr key={i} className={`border-t border-[#e2eaea] ${i % 2 === 0 ? "bg-white" : "bg-[#f8fafa]"} hover:bg-[#eaf4f3] transition-colors`}>
                    <td className="px-5 py-3 text-[#374747] font-medium">{row.method}</td>
                    <td className="px-5 py-3 text-[#607d7b]">{row.time}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="flex gap-3">
          <span className="flex-shrink-0 w-1 self-stretch bg-[#ff601e] rounded-full opacity-60" />
          <p className="text-[13px] sm:text-[14px] text-[#374747] leading-[1.85] text-justify">
            <span className="font-semibold">1.4 </span>
            We reserve the right to claim back any refund processed as a result of fraudulent activities such as return of wrong products or wrong quantity.
          </p>
        </div>
      </div>
    ),
  },
  {
    id: "cashback",
    label: "D",
    title: "Assured Delivery Cashback Eligibility",
    color: "bg-[#eaf4f3] text-[#1e4a46]",
    activeColor: "bg-[#1e4a46] text-white",
    content: (
      <div className="flex flex-col gap-3">
        <div className="bg-[#eaf4f3] border border-[#d4ecea] rounded-xl p-4">
          <ul className="flex flex-col gap-2.5">
            {[
              "1.1  Delivery assurance is for the date of delivery and not for the time of delivery.",
              "1.2  Cashback is applicable only for orders with value more than INR 1,000/-.",
              "1.3  Applicable in select pin-codes only; the customer shall be informed of eligibility at checkout.",
              "1.4  If eligible, cashback will be processed within 48 hours of order delivery/completion.",
              "1.5  Orders where delay is from customer's end will not be considered for the cashback.",
            ].map((item, i) => (
              <li key={i} className="flex items-start gap-2.5 text-[13px] sm:text-[14px] text-[#374747] leading-relaxed">
                <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-[#5fbdb3] mt-1.5" />
                {item}
              </li>
            ))}
          </ul>
        </div>
        <p className="text-[12px] text-[#607d7b] italic">
          * Local Cities are Hyderabad, Mumbai, Delhi, Kolkata, Bengaluru, Chennai, Jaipur and Ahmedabad.
        </p>
      </div>
    ),
  },
];

const CancellationPolicy = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const [activeSection, setActiveSection] = useState(null);

  return (
    <div className="min-h-screen bg-[#f8fafa] font-poppins mt-36 lg:mt-0">
      <Helmet>
        <title>Cancellation, Returns and Refund Policy | MediSYS Pharmacy</title>
        <meta name="description" content="Read MediSYS Pharmacy's Cancellation, Returns and Refund Policy including cancellation windows, non-returnable items, return process, refund timelines, and assured delivery cashback eligibility." />
        <meta name="robots"      content="index, follow" />
        <meta property="og:title"       content="Cancellation, Returns and Refund Policy | MediSYS Pharmacy" />
        <meta property="og:description" content="Cancellation, returns and refund timelines, eligibility, non-returnable items, and assured delivery cashback." />
      </Helmet>

      {/* ── Hero strip ── */}
      <div className="bg-[#162c2a] px-5 sm:px-8 lg:px-14 py-10 relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.04] pointer-events-none"
          style={{ backgroundImage: plusPattern, backgroundRepeat: "repeat", backgroundSize: "40px 40px" }}
        />
        <div className="relative">
          <div className="flex items-center gap-3 mb-3">
            <span className="w-6 h-[2px] bg-[#ff601e] rounded-full" />
            <span className="text-[11px] font-semibold uppercase tracking-widest text-[#5fbdb3]">Policy</span>
          </div>
          <h1 className="font-bold text-[26px] sm:text-[34px] text-white leading-tight">
            Cancellation, Returns &amp; Refund Policy
          </h1>
          <p className="mt-2 text-[13px] text-white/50 max-w-[520px]">
            Everything you need to know about cancelling orders, returning products, and receiving refunds from MediSYS Pharmacy.
          </p>
        </div>
      </div>

      {/* ── Quick nav pills ── */}
      <div className="px-5 sm:px-8 lg:px-14 py-5 bg-white border-b border-[#e2eaea]">
        <div className="flex flex-wrap gap-2">
          {sections.map((s, i) => (
            <button
              key={s.id}
              onClick={() => setActiveSection(activeSection === i ? null : i)}
              className={`flex items-center gap-2 px-4 h-9 rounded-xl text-[12px] font-semibold border transition-all duration-200 ${
                activeSection === i
                  ? "bg-[#1e4a46] text-white border-[#1e4a46] shadow-[0_2px_8px_rgba(22,44,42,0.2)]"
                  : "bg-white text-[#374747] border-[#e2eaea] hover:border-[#5fbdb3] hover:text-[#1e4a46]"
              }`}
            >
              <span className={`w-5 h-5 rounded-md text-[10px] font-bold flex items-center justify-center ${
                activeSection === i ? "bg-white/20" : "bg-[#eaf4f3] text-[#1e4a46]"
              }`}>
                {s.label}
              </span>
              {s.title.split(" ")[0]} Policy
            </button>
          ))}
        </div>
      </div>

      {/* ── Sections ── */}
      <div className="px-5 sm:px-8 lg:px-14 py-8">
        <div className="flex flex-col gap-4">
          {sections.map((section, idx) => {
            const isOpen = activeSection === idx;
            return (
              <div
                key={section.id}
                className={`bg-white rounded-2xl border transition-all duration-200 overflow-hidden ${
                  isOpen
                    ? "border-[#5fbdb3] shadow-[0_4px_18px_rgba(22,44,42,0.1)]"
                    : "border-[#e2eaea] shadow-[0_2px_8px_rgba(22,44,42,0.05)] hover:border-[#c5d5d4]"
                }`}
              >
                {/* Header */}
                <button
                  className="w-full flex items-center justify-between gap-4 px-5 py-4 sm:py-5 text-left"
                  onClick={() => setActiveSection(isOpen ? null : idx)}
                >
                  <div className="flex items-center gap-3">
                    <span className={`flex-shrink-0 w-8 h-8 rounded-xl flex items-center justify-center text-[13px] font-bold transition-colors duration-200 ${
                      isOpen ? "bg-[#1e4a46] text-white" : "bg-[#eaf4f3] text-[#1e4a46]"
                    }`}>
                      {section.label}
                    </span>
                    <h3 className={`text-[14px] sm:text-[15px] font-semibold transition-colors duration-200 ${
                      isOpen ? "text-[#1e4a46]" : "text-[#374747]"
                    }`}>
                      {section.title}
                    </h3>
                  </div>
                  <span className={`flex-shrink-0 w-8 h-8 rounded-xl flex items-center justify-center transition-all duration-200 ${
                    isOpen ? "bg-[#1e4a46] text-white rotate-180" : "bg-[#f1f5f5] text-[#8faba9]"
                  }`}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M6 9l6 6 6-6" />
                    </svg>
                  </span>
                </button>

                {/* Content */}
                {isOpen && (
                  <div className="px-5 pb-6 border-t border-[#f1f5f5] pt-5 pl-16">
                    {section.content}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* ── Bottom CTA ── */}
        <div className="mt-8 bg-[#162c2a] rounded-2xl p-6 relative overflow-hidden">
          <div
            className="absolute inset-0 opacity-[0.04] pointer-events-none"
            style={{ backgroundImage: plusPattern, backgroundRepeat: "repeat", backgroundSize: "40px 40px" }}
          />
          <div className="relative flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <p className="font-bold text-[15px] sm:text-[17px] text-white mb-1">Need help with a return or refund?</p>
              <p className="text-[12px] text-white/50">Our support team is available to assist you.</p>
            </div>
            <Link
              to="/contact-us"
              className="flex-shrink-0 flex items-center gap-2 bg-[#ff601e] hover:bg-[#e55519] text-white font-semibold text-[13px] px-5 h-10 rounded-xl transition-all duration-200 shadow-[0_3px_12px_rgba(255,96,30,0.35)] hover:-translate-y-0.5 whitespace-nowrap"
            >
              Contact Us
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12h14M12 5l7 7-7 7"/>
              </svg>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CancellationPolicy;